import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useGetWaivers from './useGetWaivers';
import { ROUTES } from '../../constants/global';

function useSelectWaiver() {
  const { waiverId } = useParams<{ waiverId: string | undefined }>();
  const navigate = useNavigate();

  const { data: waivers, isFetching } = useGetWaivers();

  const defaultWaiver = React.useMemo(
    () =>
      waivers?.find(waiver => waiver.status === 'DRAFT' && waiver.isDefault),
    [waivers],
  );

  const selectedDraftWaiver = React.useMemo(() => {
    const tempSelectedWaiver = waivers?.find(waiver => waiver.id === waiverId);
    if (!tempSelectedWaiver) return null;
    if (tempSelectedWaiver.status === 'DRAFT') return tempSelectedWaiver;
    const draftWaiver = waivers?.find(
      waiver =>
        waiver.status === 'DRAFT' && waiver.name === tempSelectedWaiver.name,
    );
    return draftWaiver ?? null;
  }, [waivers, waiverId]);

  const selectedPublishedWaiver = React.useMemo(() => {
    if (!selectedDraftWaiver) return null;
    return waivers?.find(
      waiver =>
        waiver.status === 'PUBLISHED' &&
        waiver.name === selectedDraftWaiver.name,
    );
  }, [waivers, selectedDraftWaiver]);

  const onSelectWaiver = (
    newWaiverId: string | null,
    { isNew, submissions }: { isNew?: boolean; submissions?: boolean } = {},
  ) => {
    const searchParams = new URLSearchParams();

    if (submissions) {
      searchParams.set('tab', 'submissions');
    }

    const searchString =
      searchParams.toString().length > 0 ? `?${searchParams.toString()}` : '';

    if (!newWaiverId) {
      navigate(ROUTES.waivers);
      return;
    }

    // new waivers may not be in the options yet
    if (isNew) {
      navigate(`${ROUTES.waivers}/${newWaiverId}${searchString}`);
    } else {
      const newWaiver = waivers?.find(waiver => waiver.id === newWaiverId);
      if (!newWaiver) return;
      const newDraftWaiver =
        newWaiver.status === 'DRAFT'
          ? newWaiver
          : waivers?.find(
              waiver =>
                waiver.status === 'DRAFT' && waiver.name === newWaiver.name,
            );
      navigate(
        `${ROUTES.waivers}/${
          newDraftWaiver?.id || defaultWaiver?.id || ''
        }${searchString}`,
      );
    }
  };

  React.useEffect(() => {
    if (isFetching || !waivers || !waiverId) return;
    const existingWaiver = waivers?.find(waiver => waiver.id === waiverId);
    if (!existingWaiver) {
      navigate(`${ROUTES.waivers}`);
    } else if (existingWaiver.status !== 'DRAFT') {
      const draftWaiver = waivers?.find(
        waiver =>
          waiver.status === 'DRAFT' && waiver.name === existingWaiver.name,
      );
      if (!draftWaiver) {
        navigate(ROUTES.waivers);
      } else {
        navigate(`${ROUTES.waivers}/${draftWaiver?.id}`);
      }
    }
  }, [waivers, isFetching, navigate, waiverId]);

  return {
    waiverId,
    waiver: selectedDraftWaiver,
    publishedWaiver: selectedPublishedWaiver,
    defaultWaiver,
    onSelectWaiver,
  };
}

export default useSelectWaiver;
